/**
 * Wallets plan code fix 2: when a card or wallet payment hands off to another page
 * (3D Secure, a wallet) and comes back, Stripe adds the payment's secret to the
 * page's address (`payment_intent_client_secret`, `setup_intent_client_secret`).
 * Telify never reads it (the payment step finds its payment from its own record),
 * so it is dropped from the address bar before anything else reads the address:
 * not left in history, a copied link, or a later request's address.
 *
 * The same address names the payment it went to make (`payment_intent`, not a
 * secret) and how it ended (`redirect_status`). That payment may have gone through
 * while the client was away, its held time already turned into the booking. So the
 * return is kept for the page: the payment step reads that payment first, and the
 * held-time prompts wait, until it settles (Codex re-check 2026-10-26).
 */
import { useSyncExternalStore } from 'react';

const SECRET_PARAMS = ['payment_intent_client_secret', 'setup_intent_client_secret'] as const;
/** The endings worth reading back: a failed hand-off goes back to the card form as before. */
const RESUMABLE = new Set(['succeeded', 'processing']);

let returning: string | null = null;
const listeners = new Set<() => void>();

/** The address without Stripe's secrets (path, query and hash), or null when it carries none. */
export function withoutStripeSecrets(href: string): string | null {
  const url = new URL(href);
  if (!SECRET_PARAMS.some((p) => url.searchParams.has(p))) return null;
  for (const p of SECRET_PARAMS) url.searchParams.delete(p);
  return `${url.pathname}${url.search}${url.hash}`;
}

/** Notes a return from Stripe, then drops its secrets from the address bar in place; never stops the page from loading. */
export function dropStripeReturnSecrets(): void {
  try {
    const url = new URL(window.location.href);
    const payment = url.searchParams.get('payment_intent');
    if (payment && RESUMABLE.has(url.searchParams.get('redirect_status') ?? '')) returning = payment;
    const next = withoutStripeSecrets(url.href);
    if (next !== null) window.history.replaceState(window.history.state, '', next);
  } catch {
    /* an address the browser will not rewrite: the page still loads */
  }
}

/** The payment this page load came back from Stripe with, until it settles; null otherwise. */
export function stripeReturnPayment(): string | null {
  return returning;
}

/** The return is settled (its booking opened, or the payment lapsed or was replaced): the page goes on as usual. */
export function settleStripeReturn(): void {
  if (returning === null) return;
  returning = null;
  for (const listener of listeners) listener();
}

/** The return, for a component that waits on it. */
export function useStripeReturnPayment(): string | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    stripeReturnPayment,
  );
}
